import * as React from 'react';
import { useState } from 'react';
import { Input, Popover } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { ContactAvatar } from '../contact_avatar/ContactAvatar';
import { cn } from '../../utils/ClassNamesUtils';
import { nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getContactName } from '../../utils/DataFormatterUtils';

// The one way a task gets its assignee anywhere inside projects: a click-triggered
// popover with a server-side search box and the contact list (avatar / name / email),
// plus "No Assignee". Shared by project/deliverable tasks (DeliverableTasks) and
// template tasks (ProjectTemplateItemFields), so the two cannot drift.
//
// The caller owns the contacts: it passes the current page of results and an
// onSearch that refetches them. onOpenChange lets a caller with many rows fetch only
// when a popover actually opens, rather than once per row on mount.

interface ProjectTaskAssignPopoverProps {
	email?: string;
	contacts: any[];
	onSearch: (term: string) => void;
	onChange: (email: string | null) => void;
	onOpenChange?: (open: boolean) => void;
	children: React.ReactNode;
}

const contactName = (contact) =>
	getContactName(contact).trim() || nullSafeGetOrElse('email', contact, '');

export const ProjectTaskAssignPopover: React.FC<ProjectTaskAssignPopoverProps> = ({
	email,
	contacts,
	onSearch,
	onChange,
	onOpenChange,
	children,
}) => {
	const [open, setOpen] = useState(false);

	const handleVisibleChange = (next) => {
		setOpen(next);
		if (onOpenChange) {
			onOpenChange(next);
		}
	};

	const pick = (next) => {
		onChange(next);
		handleVisibleChange(false);
	};

	const pickNobody = () => pick(null);

	const handleSearchChange = (event) => onSearch(event.target.value);

	const content = (
		<div className="w-[288px]">
			<div className="footnoteregular mb-2 text-character-secondary">ASSIGN TO</div>
			<Input
				allowClear
				placeholder="Search people"
				className="mb-2"
				onChange={handleSearchChange}
			/>
			<div className="max-h-64 overflow-y-auto">
				<div
					className="flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 hover:bg-grey-2"
					onClick={pickNobody}
				>
					<span className="flex size-8 items-center justify-center rounded-full bg-grey-6 text-white">
						<UserOutlined />
					</span>
					<span className="bodyregular text-character-primary">No Assignee</span>
				</div>
				{(contacts || []).map((contact) => {
					const contactEmail = nullSafeGetOrElse('email', contact, '');
					return (
						<div
							key={contactEmail}
							onClick={() => pick(contactEmail)}
							className={cn(
								'flex cursor-pointer items-center gap-2 rounded px-1 py-1.5 hover:bg-grey-2',
								contactEmail === email && 'bg-purple-1'
							)}
						>
							<ContactAvatar contact={contact} size="small" hidePopover />
							<div className="flex flex-col">
								<span className="bodyregular text-character-primary">{contactName(contact)}</span>
								<span className="footnoteregular text-character-secondary">{contactEmail}</span>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);

	return (
		<Popover
			trigger="click"
			visible={open}
			onVisibleChange={handleVisibleChange}
			// Callers refetch the unfiltered list on open, so the search box must start
			// empty again too — otherwise it shows the last term over unfiltered results.
			destroyTooltipOnHide
			content={content}
			placement="bottomRight"
		>
			{children}
		</Popover>
	);
};
